import { DEFAULT_APP_LOCALE } from './@core/constants/i18n';
import { Language } from './@core/models';
import { getLocalizedLanguageName, registerLocales } from '@/@core/services/localization';
import { getInitialLocale } from '@/@core/utils/getInitialLocale';
import { Locale } from '@/stores/useWorkspaceStore';
import { createI18n } from 'vue-i18n';

registerLocales();

const i18n = createI18n({
  locale: getInitialLocale(),
  fallbackLocale: DEFAULT_APP_LOCALE,
  legacy: false,
  messages: {
    en: {},
    de: {},
  },
  fallbackWarn: false,
  missingWarn: false,
  datetimeFormats: {
    en: {
      short: {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      },
      long: {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        weekday: 'short',
        hour: 'numeric',
        minute: 'numeric',
      },
    },
    'en-GB': {
      short: {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      },
      long: {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        weekday: 'short',
        hour: 'numeric',
        minute: 'numeric',
      },
    },
    de: {
      short: {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      },
      long: {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23', // 'hour12: false' leads to the midnight hour being displayed as 24 instead of 00, see the discussion in https://github.com/moment/luxon/issues/726
      },
    },
  },
});

export const setLocalMessages = async (
  locale: string | undefined,
  directory: string | undefined = undefined
) => {
  const messages = await (directory !== undefined
    ? import(`./locales/${directory}/${locale}.json`)
    : import(`./locales/${locale}.json`));

  Object.entries(messages.default).forEach(([locale, messages]: any) => {
    const existingMessages = i18n.global.getLocaleMessage(locale) || {};
    i18n.global.setLocaleMessage(locale, { ...existingMessages, ...messages });
  });
};

export const translateLocaleOutsideVue = async (key: string, value?: any) => {
  const localeFileName = key.split('.').shift()?.toLowerCase().replaceAll('_', '-');
  await setLocalMessages(localeFileName);
  return i18n.global.t(key, value);
};

export const t = (key: string, value?: any) => i18n.global.t(key, value);

export const supportedLocales = (locale: Locale): Language[] =>
  [...new Set([...i18n.global.availableLocales])].map((localeCode) => ({
    code: localeCode,
    languageName: getLocalizedLanguageName(localeCode, locale),
  }));

export default i18n;
